import { Grid, useTheme, Box, Typography, CircularProgress, Card, CardContent, alpha, Divider } from '@mui/material';
import React, { useEffect, useState } from 'react';
import CSPDetails from './CSPDetails';
import ShipmentSummaryPickupType from './ShipmentSummaryPickupType';
import SbTextField from '../../../components/Textfield';
import Charges from './Charges';
import CreateShipmentContext from '../../../context/CreateShipment';
import { createShipmentContextType } from '../../../types/shipment';
import { AppDispatch, RootStateType } from '../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { placeShipment, redirectToSuccessPage, resetEditShipment } from '../../../store/reducers/Shipment';
import {
  ShipperConsigneeSummaryDetails,
  EditAction,
} from '../../../components/Shipment/Summary/ShipperConsigneeSummaryDetails';
import ShipmentSummaryDetails from '../../../components/Shipment/Summary/ShipmentSummaryDetails';
import { ShipmentType } from '../../../store/reducers/Shipment/Type';
import { PayNow } from '@Components/PayNow';
import { getAddressObj, getSingleShipmentPackagePayload } from '@Services/getShipmentPayload';
import { usePayment } from '@Hook/usePayment';
import SbButton from '@Components/Button';
import { ErrorType } from './Type';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import API from '@Services/apiAxios';
import { isDutyChargesAllowedSubdomain } from '../../../utils/getSubdomain';
import { getDutyCharge } from '../../../utils/pricing';

const getDutyChargeType = (dutyCharge: any): string => {
  if (!dutyCharge) return '';
  if (typeof dutyCharge === 'string') return dutyCharge;
  if (typeof dutyCharge === 'object' && typeof dutyCharge.type === 'string') return dutyCharge.type;
  return '';
};

function ShipmentSummary() {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const { setOpenPaymentModal, hasPaymentAccess } = usePayment();
  const {
    shipper,
    consignee,
    shipmentType,
    extraDetails,
    packageData,
    notes,
    boxes,
    pickupType,
    setNotes,
    setShipper,
    setConsignee,
    userId,
    selectedCSP,
    setActiveStep,
  } = React.useContext(CreateShipmentContext) as createShipmentContextType;

  const {
    shipmentSummary,
    editShipment,
    finalChargeableWeight,
    loading: placementLoading,
  } = useSelector((state: RootStateType) => state.shipment);
  const { shipmentSetting, organizationPublicData } = useSelector((state: RootStateType) => state?.orgSetup);
  const CSPChargesData = useSelector((state: RootStateType) => state?.getRates?.CSPChargesData);

  const orgCountryName = organizationPublicData?._country?.name?.trim() || '';

  const getCountryName = (field: any): string => {
    if (!field) return '';
    if (typeof field === 'string') return field.trim();
    if (typeof field === 'object') {
      if (typeof field.country === 'string') return field.country.trim();
      if (field.country && typeof field.country === 'object' && typeof field.country.name === 'string') {
        return field.country.name.trim();
      }
    }
    return '';
  };

  const shipperCountry = getCountryName(shipper);
  const consigneeCountry = getCountryName(consignee);

  const [loadingDocs, setLoadingDocs] = useState<boolean>(false);
  const [clearanceDocs, setClearanceDocs] = useState<string[]>([]);
  const [docsError, setDocsError] = useState<string | null>(null);

  const formatPickupDate = (date: any): string => {
    if (!date) return '';
    if (typeof date.toDate === 'function') {
      return date.toDate().toUTCString();
    }
    const parsed = new Date(date);
    if (!isNaN(parsed.getTime())) {
      return parsed.toUTCString();
    }
    return String(date);
  };

  const pricingDuty = getDutyCharge(selectedCSP?.pricing);
  const dutyChargeAmount =
    pricingDuty > 0
      ? pricingDuty
      : CSPChargesData?.dutyChargeAmount !== undefined && CSPChargesData?.dutyChargeAmount !== null
      ? Number(CSPChargesData.dutyChargeAmount)
      : 0;

  const dutyChargeType = getDutyChargeType(shipmentType.dutyCharge);

  const requestPayload = React.useMemo(() => {
    return {
      consignee: {
        country: consigneeCountry || '',
      },
      package: {
        unitOfMeasurement: packageData?.unitOfMeasurement || shipmentType?.unit || '',
        packaging: !!packageData?.packaging,
        insurance: !!packageData?.insurance,
        isDocument: shipmentType?.type === 'document',
        isCod: !!packageData?.isCod,
        isDropOff: pickupType?.type === 'dropOff',
        pickupDate: formatPickupDate(pickupType?.date),
        timeSlot: pickupType?.timeSlot || '',
        codAmount: Number(packageData?.codAmount) || 0,
        currency: shipmentType?.currency || '',
        box: (boxes || []).map((box, idx) => ({
          length: Number(box.length) || 0,
          width: Number(box.width) || 0,
          height: Number(box.height) || 0,
          weight: Number(box.weight) || 0,
          id: box.id || idx.toString(),
          products: (box.products || []).map((prod: any) => ({
            description: prod.description || '',
            quantity: Number(prod.quantity) || 0,
            unitValue: Number(prod.unitValue) || 0,
            HSNName: prod.HSNName || '',
            HSNCode: prod.HSNCode || '',
            productId: prod.productId ? String(prod.productId).trim() : '',
            productIdType: prod.productIdType ? String(prod.productIdType).trim() : 'SKU',
            merchantProductId: prod.merchantProductId ? String(prod.merchantProductId).trim() : '',
            standardManufacturerProductId: prod.standardManufacturerProductId
              ? String(prod.standardManufacturerProductId).trim()
              : '',
            nonStandardManufacturerProductId: prod.nonStandardManufacturerProductId
              ? String(prod.nonStandardManufacturerProductId).trim()
              : '',
            _product: prod._product || null,
            _id: prod._id || prod.id || '',
            createdAt: prod.createdAt || new Date().toISOString(),
            updatedAt: prod.updatedAt || new Date().toISOString(),
          })),
        })),
        totalBoxWeight: 'Need calc',
        invoiceNumber: shipmentType?.invoiceNumber || packageData?.invoiceNumber || '',
        ...(isDutyChargesAllowedSubdomain() &&
          dutyChargeType && {
            dutyCharge: {
              type: dutyChargeType,
              charge: isNaN(dutyChargeAmount) ? 0 : dutyChargeAmount,
            },
          }),
      },
      purposeOfShipment: extraDetails?.purposeOfShipment || 'FOR_SALE',
    };
  }, [
    consigneeCountry,
    packageData,
    shipmentType,
    pickupType,
    boxes,
    extraDetails?.purposeOfShipment,
    dutyChargeType,
    dutyChargeAmount,
  ]);

  const requestPayloadStr = React.useMemo(() => JSON.stringify(requestPayload), [requestPayload]);

  useEffect(() => {
    if (!consigneeCountry) return;

    let active = true;
    const fetchClearanceDocs = async () => {
      setLoadingDocs(true);
      setDocsError(null);
      try {
        const response = await API.post('/speedboxbot/clearance-documents', JSON.parse(requestPayloadStr));
        if (active) {
          const docs = response?.data?.documents || [];
          const docNames = docs.map((doc: any) => doc.documentName).filter(Boolean);
          setClearanceDocs(docNames);
        }
      } catch (err) {
        console.error('Error fetching clearance documents:', err);
        if (active) {
          setDocsError('Failed to fetch required clearance documents.');
        }
      } finally {
        if (active) {
          setLoadingDocs(false);
        }
      }
    };

    fetchClearanceDocs();
    return () => {
      active = false;
    };
  }, [requestPayloadStr, consigneeCountry]);

  const checkCountries = [shipperCountry, consigneeCountry].filter(Boolean);
  const isShipmentExport = orgCountryName
    ? checkCountries.some(c => c.toLowerCase() !== orgCountryName.toLowerCase())
    : false;

  const { pickupTimeSlots } = shipmentSetting || {};
  const [errors, setErrors] = useState<ErrorType>();

  const shipmentNumber = shipmentSummary?.shipmentNumber || '';
  const sellingPricing = selectedCSP?.totalPricing;

  const { warehouse, selectedWearhouseAddress } = pickupType;
  const { shipmentMode, referenceNumber } = shipmentType;
  const { insurance, packaging, unitOfMeasurement } = packageData;

  useEffect(() => {
    return () => {
      dispatch(resetEditShipment());
    };
  }, []);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setNotes(value);
  }

  function handleDetailsChange(event: React.ChangeEvent<HTMLInputElement>, label: string) {
    const { value } = event.target;
    if (label === 'Shipper') {
      setShipper({ ...shipper, name: value });
    } else if (label === 'Consignee') {
      setConsignee({ ...consignee, name: value });
    }
  }

  function handlePlaceOrder() {
    const errorData: ErrorType = {};
    if (!shipper.name) {
      errorData['shipperName'] = 'Please add name.';
      setErrors(errorData);
    }
    if (!consignee.name) {
      errorData['consigneeName'] = 'Please add name.';
      setErrors(errorData);
    }
    if (pickupType.type === 'dropOff' && !pickupType.warehouse) {
      errorData['warehouse'] = 'Please choose the drop-off warehouse.';
      setErrors(errorData);
      return;
    } else if (pickupType.type === 'pickUp') {
      if (!pickupType.date) {
        errorData['date'] = 'Please choose a pickup date ';
        setErrors(errorData);
        return;
      }
      if (pickupTimeSlots?.slots?.length && !pickupType.timeSlot) {
        errorData['timeSlot'] = 'Please choose a  time slot.';
        setErrors(errorData);
        return;
      }
    }

    if ((!errorData || !Object.keys(errorData).length) && editShipment) {
      setErrors(undefined);
      let packageObj = getSingleShipmentPackagePayload({ boxes, packageData, shipmentType, pickupType });
      const hurricaneProducts = CSPChargesData?.hurricaneData?.products;
      if (isShipmentExport && Array.isArray(hurricaneProducts) && hurricaneProducts.length > 0) {
        packageObj = JSON.parse(JSON.stringify(packageObj));
        if (Array.isArray(packageObj.box)) {
          packageObj.box.forEach((box: any) => {
            if (Array.isArray(box.products)) {
              box.products.forEach((prod: any) => {
                const match = hurricaneProducts.find(
                  (hp: any) => hp.description?.toLowerCase().trim() === prod.description?.toLowerCase().trim(),
                );
                if (match && match.suitableImportCode) {
                  prod.HSNCode = match.suitableImportCode;
                }
              });
            }
          });
        }
      }

      if (isDutyChargesAllowedSubdomain() && getDutyChargeType(shipmentType.dutyCharge)) {
        const pricingDuty = getDutyCharge(selectedCSP?.pricing);
        const dutyChargeAmount =
          pricingDuty > 0
            ? pricingDuty
            : CSPChargesData?.dutyChargeAmount !== undefined && CSPChargesData?.dutyChargeAmount !== null
            ? Number(CSPChargesData.dutyChargeAmount)
            : 0;

        const formattedDutyCharge = {
          type: getDutyChargeType(shipmentType.dutyCharge),
          charge: isNaN(dutyChargeAmount) ? 0 : dutyChargeAmount,
        };

        packageObj.dutyCharge = formattedDutyCharge;
      }

      const postObj: ShipmentType = {
        package: packageObj,
        shipper: getAddressObj(shipper),
        consignee: getAddressObj(consignee),
        shipmentNotes: notes,
        shipmentMode: shipmentType.shipmentMode,
        userReferenceNumber: shipmentType.referenceNumber || '',
        selectedCourierId: selectedCSP?._courierId,
        selectedSystemId: selectedCSP?._systemId,
        ...(isDutyChargesAllowedSubdomain() &&
          getDutyChargeType(shipmentType.dutyCharge) && {
            dutyCharge: {
              type: getDutyChargeType(shipmentType.dutyCharge),
              charge: isNaN(dutyChargeAmount) ? 0 : dutyChargeAmount,
            },
          }),
        extraDetails: {
          shipper: {
            type: shipper.type,
            IdType: shipper.IdType,
            IdNumber: shipper.IdNumber,
          },
          consignee: {
            type: consignee.type,
            IdType: consignee.IdType,
            IdNumber: consignee.IdNumber,
          },
          other: {
            ...extraDetails,
            incoterm: shipmentType.incoterm || extraDetails?.incoterm || 'FOB',
            freight:
              shipmentType.freight !== undefined
                ? Number(shipmentType.freight)
                : extraDetails?.freight !== undefined
                ? Number(extraDetails.freight)
                : 0,
            freightInsurance:
              shipmentType.freightInsurance !== undefined
                ? Number(shipmentType.freightInsurance)
                : extraDetails?.freightInsurance !== undefined
                ? Number(extraDetails.freightInsurance)
                : 0,
            iossNumber: shipmentType.iossNumber || extraDetails?.iossNumber || '',
            recipientVatTaxId: shipmentType.recipientVatTaxId || extraDetails?.recipientVatTaxId || '',
            iorEoriNumber: shipmentType.iorEoriNumber || extraDetails?.iorEoriNumber || extraDetails?.ior || '',
            ior: shipmentType.iorEoriNumber || extraDetails?.ior || '',
          },
        },
      };
      if (pickupType.type === 'dropOff') {
        postObj.warehouseAddress = selectedWearhouseAddress;
        postObj.warehouseId = warehouse;
      }
      dispatch(placeShipment({ postObj: { ...postObj }, userId, showPaymentModal: hasPaymentAccess }));
    } else {
      setOpenPaymentModal(true);
    }
  }

  function handlePaymentSuccess() {
    dispatch(redirectToSuccessPage(true));
  }

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Header Banner */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
          Review Shipment Summary
        </Typography>
        <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
          Please verify all shipper, consignee, package, and pricing details before placing your shipment.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column (Summary Cards) */}
        <Grid item xs={12} lg={8}>
          <Grid container spacing={2.5}>
            {/* 1. Selected Service & Carrier Card */}
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: '14px',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                    Selected Courier Service
                  </Typography>
                  {editShipment && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EditAction activeStep={1} />
                    </Box>
                  )}
                </Box>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <CSPDetails />
                </CardContent>
              </Card>
            </Grid>

            {/* 2. Parties Card (Shipper & Consignee Side-by-Side) */}
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: '14px',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                    Party Information
                  </Typography>
                  {editShipment && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EditAction activeStep={0} />
                    </Box>
                  )}
                </Box>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      sx={{ borderRight: { sm: `1px solid ${alpha(theme.palette.divider, 0.08)}` } }}
                    >
                      <ShipperConsigneeSummaryDetails
                        label="Shipper"
                        {...shipper}
                        activeStep={0}
                        handleChange={handleDetailsChange}
                        editShipment={editShipment}
                        error={errors?.shipperName || ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ShipperConsigneeSummaryDetails
                        label="Consignee"
                        {...consignee}
                        activeStep={0}
                        handleChange={handleDetailsChange}
                        editShipment={editShipment}
                        error={errors?.consigneeName || ''}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* 3. Dispatch / Pickup Details Card */}
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: '14px',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <ShipmentSummaryPickupType errorData={errors} />
                </CardContent>
              </Card>
            </Grid>

            {/* 4. Shipment Details & Add a Note Card */}
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: '14px',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ borderRight: { md: `1px solid ${alpha(theme.palette.divider, 0.08)}` } }}
                    >
                      <ShipmentSummaryDetails
                        boxes={boxes.length}
                        chargeableWeight={finalChargeableWeight}
                        shipmentType={shipmentType.type}
                        shipmentMode={shipmentMode}
                        insurance={insurance}
                        packaging={packaging}
                        unitOfMeasurement={unitOfMeasurement}
                        activeStep={1}
                        editShipment={editShipment}
                        referenceNumber={referenceNumber}
                        purposeOfShipment={extraDetails?.purposeOfShipment}
                        ior={shipmentType?.iorEoriNumber || extraDetails?.ior}
                        incoterm={shipmentType?.incoterm}
                        freight={shipmentType?.freight}
                        freightInsurance={shipmentType?.freightInsurance}
                        iossNumber={shipmentType?.iossNumber}
                        recipientVatTaxId={shipmentType?.recipientVatTaxId}
                        currency={shipmentType?.currency}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1.5, color: 'common.black' }}>
                        Shipment Notes & Instructions
                      </Typography>
                      <SbTextField
                        placeholder="Add special instructions, gate entry codes, or package notes..."
                        onChange={handleChange}
                        name="description"
                        value={notes}
                        rows={6}
                        multiline
                        className="w-full"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* 5. Required Clearance Documents Card */}
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: '14px',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'primary.main',
                      }}
                    >
                      <DescriptionIcon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                        Required Clearance Documents
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
                        Documentation needed for customs and courier processing
                      </Typography>
                    </Box>
                  </Box>
                  {!loadingDocs && !docsError && clearanceDocs.length > 0 && (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '16px',
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: 'primary.main',
                        fontSize: '12px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                      }}
                    >
                      {clearanceDocs.length} {clearanceDocs.length === 1 ? 'Document' : 'Documents'}
                    </Box>
                  )}
                </Box>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  {loadingDocs ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2.5 }}>
                      <CircularProgress size={20} thickness={4} />
                      <Typography sx={{ color: 'text.secondary', fontSize: '13.5px' }}>
                        Fetching required clearance documents...
                      </Typography>
                    </Box>
                  ) : docsError ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1.5,
                        px: 2,
                        borderRadius: '8px',
                        bgcolor: alpha(theme.palette.error.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                      <InfoOutlinedIcon color="error" sx={{ fontSize: '20px', flexShrink: 0 }} />
                      <Typography sx={{ color: 'error.main', fontSize: '13px', fontWeight: 500 }}>
                        Failed to load required clearance documents. You can still proceed with placing the shipment.
                      </Typography>
                    </Box>
                  ) : clearanceDocs.length === 0 ? (
                    <Typography sx={{ color: 'text.secondary', fontSize: '13px', py: 1 }}>
                      No specific clearance documents are required for this shipment.
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        columnGap: 4,
                        rowGap: 0,
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                      {clearanceDocs.map((docName, idx) => (
                        <Box
                          key={`${docName}-${idx}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            py: 1.25,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            minWidth: 0,
                            boxSizing: 'border-box',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              color: 'primary.main',
                              flexShrink: 0,
                              mr: 1.5,
                            }}
                          >
                            <DescriptionIcon sx={{ fontSize: '14px' }} />
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: '13.5px',
                              color: 'text.primary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                            }}
                            title={docName}
                          >
                            {docName}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Column (Sticky Pricing & Place Order Card) */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ position: { lg: 'sticky' }, top: 24 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: '14px',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 12px -2px rgba(0, 0, 0, 0.03)',
                bgcolor: theme.palette.background.paper,
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Charges />

                <Divider sx={{ my: 3, borderColor: alpha(theme.palette.divider, 0.08) }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {!hasPaymentAccess ? (
                    <SbButton
                      onClick={handlePlaceOrder}
                      variant="contained"
                      disabled={placementLoading}
                      startIcon={placementLoading ? <CircularProgress size={18} color="inherit" /> : null}
                      sx={{
                        width: '100%',
                        py: 1.4,
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        borderRadius: '10px',
                        boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                      }}
                    >
                      {placementLoading ? 'Placing Shipment...' : 'Confirm & Place Shipment'}
                    </SbButton>
                  ) : (
                    <PayNow
                      handlePayNow={handlePlaceOrder}
                      paymentDetails={{
                        shipmentNumber,
                        payableAmount: Number(sellingPricing),
                        handlePaymentSuccess,
                      }}
                    />
                  )}

                  <SbButton
                    onClick={() => setActiveStep(1)}
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      width: '100%',
                      py: 1,
                      fontWeight: 600,
                      borderRadius: '8px',
                    }}
                  >
                    Back to Shipment Details
                  </SbButton>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                    <VerifiedUserOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Secure Transaction · Instant AWB Confirmation
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ShipmentSummary;
